/**
 * Filtros com contagem à vista.
 *
 * Faceta sem número obriga a tentar para descobrir se dá em nada. Com o
 * número, o usuário sabe antes de clicar — e vê a forma do acervo: 26 UFs
 * concentradas em MG, SC e RS; 439 triagens contra 33 análises profundas.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): a contagem redonda (`Conta`), o "Limpar
 * tudo" (o botão `discreto`), o "Mostrar mais" (o botão `texto`) e os ícones
 * do funil e da seta saem de `src/ds`. O resto — a coluna, os grupos e as
 * linhas de filtro com a contagem — é desta peça.
 */
import { useState } from "react";
import type { Contagem, Facetas as Dados, Filtros } from "../api/cliente";
import { Botao, Conta, IconeChevron, IconeFunil } from "../ds";
import css from "./Facetas.module.css";

interface Props {
  dados?: Dados;
  filtros: Filtros;
  aoMudar: (m: Partial<Filtros>) => void;
}

// "recomendacao" não entra: virou as abas do topo. Duas superfícies para o
// mesmo filtro é onde o usuário perde a noção do que está aplicado.
const GRUPOS = [
  // A ORDEM FOI PEDIDA PELA GESTAO, 2026-09-16: primeiro a palavra que trouxe
  // a oportunidade, depois o estado, e so entao o resto.
  { chave: "termo", titulo: "Encontrada por", limite: 8 },
  { chave: "uf", titulo: "Estado", limite: 8 },
  { chave: "categoria", titulo: "Linha de serviço", limite: 10 },
  { chave: "aderencia", titulo: "Aderência" },
  { chave: "nivel", titulo: "Profundidade da análise" },
  { chave: "edital", titulo: "Estado do edital" },
  { chave: "decisao", titulo: "Decisão" },
] as const;

export function Facetas({ dados, filtros, aoMudar }: Props) {
  // Quais grupos estao abertos por inteiro. Antes do `return` antecipado,
  // porque hook nao pode depender de condicao.
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});
  // O bloco inteiro recolhido, abaixo de 980px (ver `.alternar`). Pela mesma
  // razao, antes do `return` antecipado.
  const [aberta, setAberta] = useState(false);
  if (!dados) return <aside className={css.rail} aria-busy="true" />;

  function alternar(chave: keyof Filtros, valor: string) {
    const atual = (filtros[chave] as string[] | undefined) ?? [];
    const novo = atual.includes(valor)
      ? atual.filter((v) => v !== valor)
      : [...atual, valor];
    aoMudar({ [chave]: novo.length ? novo : undefined } as Partial<Filtros>);
  }

  const aplicados = GRUPOS.reduce(
    (s, g) => s + ((filtros[g.chave as keyof Filtros] as string[] | undefined)?.length ?? 0),
    0,
  );
  const algumFiltro = aplicados > 0;

  return (
    <aside className={css.rail} aria-label="Filtros" data-aberta={aberta}>
      {/* `display: contents` no desktop: ali o "Limpar filtros" continua filho
          direto da coluna, como sempre foi. Abaixo de 980px vira a linha do
          botao que abre o bloco. */}
      {/* O TÍTULO DO BLOCO (20/10, escolha do Pedro): "Filtros", a contagem
          num número redondo e "Limpar tudo" na ponta. No desktop é um título;
          abaixo de 980px o título vira o botão que abre o bloco (o funil, a
          palavra, o número e a seta), e o "Limpar tudo" continua ao lado,
          para desfazer sem abrir. */}
      <div className={css.cabecalho}>
        <h2 className={css.titulo}>
          Filtros
          {aplicados > 0 && (
            <Conta rotulo={`${aplicados} ${aplicados === 1 ? "aplicado" : "aplicados"}`}>{aplicados}</Conta>
          )}
        </h2>
        <button
          type="button"
          className={css.alternar}
          aria-expanded={aberta}
          onClick={() => setAberta((a) => !a)}
        >
          <IconeFunil />
          Filtros
          {aplicados > 0 && (
            <Conta>
              {aplicados}
              <span className="apenas-leitor">{aplicados === 1 ? " aplicado" : " aplicados"}</span>
            </Conta>
          )}
          <IconeChevron tamanho={12} className={css.seta} />
        </button>
        {algumFiltro && (
          <Botao
            variante="discreto"
            className={css.limpar}
            // Derivado de GRUPOS, e não uma lista escrita à mão: a versão
            // anterior zerava quatro dos seis grupos, então "Limpar filtros"
            // deixava categoria e aderência de pé — e a contagem no topo
            // continuava recortada sem nada na tela dizendo por quê. Grupo novo
            // agora entra aqui sozinho.
            onClick={() =>
              aoMudar(
                Object.fromEntries(GRUPOS.map((g) => [g.chave, undefined])) as Partial<Filtros>,
              )
            }
          >
            Limpar tudo
          </Botao>
        )}
      </div>

      {GRUPOS.map((g) => {
        const lista = (dados[g.chave] ?? []) as Contagem[];
        if (!lista.length) return null;
        const marcados = (filtros[g.chave as keyof Filtros] as string[]) ?? [];
        const aberto = !!abertos[g.chave];
        // Marcado nunca some ao recolher: um filtro aplicado e escondido e
        // exatamente o estado em que a lista encolhe sem ninguem saber por que.
        const visiveis =
          "limite" in g && !aberto
            ? lista.filter((c, i) => i < g.limite! || marcados.includes(c.valor))
            : lista;
        return (
          <section key={g.chave} className={css.grupo}>
            <h2>{g.titulo}</h2>
            <ul>
              {visiveis.map((c) => (
                <li key={c.valor}>
                  <label className={marcados.includes(c.valor) ? css.marcado : ""}>
                    <input
                      type="checkbox"
                      checked={marcados.includes(c.valor)}
                      onChange={() => alternar(g.chave as keyof Filtros, c.valor)}
                    />
                    <span className={css.rotulo}>{c.rotulo}</span>
                    <span className={css.total}>{c.total}</span>
                  </label>
                </li>
              ))}
            </ul>
            {/* EXPANDE DE VERDADE. Era um texto "+4 com menos resultados" que
                prometia mais filtros e nao abria nada. */}
            {"limite" in g && lista.length > g.limite! && (
              <Botao
                variante="texto"
                className={css.resto}
                aria-expanded={aberto}
                onClick={() => setAbertos((a) => ({ ...a, [g.chave]: !aberto }))}
              >
                {aberto ? "Mostrar menos" : `Mostrar mais ${lista.length - g.limite!}`}
              </Botao>
            )}
          </section>
        );
      })}
    </aside>
  );
}
